import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Lock, Mail, KeyRound, ArrowRight, ShieldCheck, AlertCircle } from "lucide-react";
import { adminAuthService } from "@/api/adminAuthService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";

/**
 * ============================================================
 * DEDICATED ADMIN LOGIN PAGE (/admin/login)
 * ============================================================
 * Internal login screen authenticated directly against Supabase Auth
 * and verified with PostgreSQL `public.is_admin()` RLS check.
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const from = location.state?.from?.pathname || "/admin";

  // Ensure pure light admin theme is applied to document root while in admin login
  useEffect(() => {
    document.documentElement.classList.add("admin-theme");
    return () => {
      document.documentElement.classList.remove("admin-theme");
    };
  }, []);

  // If already authenticated with valid admin session, redirect immediately
  useEffect(() => {
    adminAuthService.checkAuth().then((adminUser) => {
      if (adminUser) {
        navigate(from, { replace: true });
      }
    });
  }, [navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    const cleanEmail = email.trim();
    const cleanPass = password.trim();

    if (!cleanEmail || !cleanPass) {
      setErrorMessage("Please enter both your administrator email and password.");
      return;
    }

    try {
      setLoading(true);
      const result = await adminAuthService.login({ email: cleanEmail, password: cleanPass });
      toast.success(result.message || "Welcome to the Shiveda Admin Console.");
      navigate(from, { replace: true });
    } catch (err) {
      const msg = err?.message || "Authentication failed. Please check your credentials.";
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col justify-between selection:bg-primary/25 selection:text-slate-900 relative overflow-hidden">
      {/* Background ambient lighting */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden bg-slate-50">
        <div className="absolute inset-0 opacity-[0.03] [background-image:repeating-linear-gradient(0deg,transparent,transparent_7.5%,rgba(0,0,0,0.8)_calc(7.5%+1px)),repeating-linear-gradient(90deg,transparent,transparent_7.5%,rgba(0,0,0,0.8)_calc(7.5%+1px))]" />
        <div className="absolute inset-0 bg-[radial-gradient(1000px_500px_at_50%_-10%,rgba(0,194,255,0.08),transparent_60%)]" />
      </div>

      {/* Top minimal bar */}
      <div className="p-6 sm:p-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center">
            <img
              src="/Shiveda_Logo.png"
              alt="Shiveda"
              className="w-6 h-auto"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
          <div>
            <div className="text-sm font-bold tracking-tight text-slate-900 flex items-center gap-2">
              Shiveda IT Services
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 font-medium font-mono">
                Admin Console
              </span>
            </div>
            <div className="text-[10px] text-slate-500 font-medium">Internal Operations Portal</div>
          </div>
        </div>

        <Link
          to="/"
          className="text-xs font-medium text-slate-500 hover:text-slate-900 transition-colors"
        >
          Return to Site
        </Link>
      </div>

      {/* Main Login Card */}
      <div className="w-full max-w-md mx-auto px-5 py-8">
        <div className="rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-8 backdrop-blur-2xl shadow-xl shadow-slate-200/50">
          {/* Card Header */}
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-2xl bg-cyan-50 border border-cyan-200 text-cyan-600 mx-auto flex items-center justify-center mb-4 shadow-xs">
              <Lock className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Administrator Access</h1>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed font-medium">
              Sign in with your verified administrator credentials to access system management.
            </p>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="mb-6 p-3 rounded-xl border border-rose-200 bg-rose-50 text-sm font-semibold text-rose-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="admin-email" className="text-xs text-slate-700 font-medium">
                Administrator Email
              </Label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="admin@shivedait.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl focus:bg-white focus:border-cyan-500 focus-visible:ring-cyan-500 text-xs"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="admin-password" className="text-xs text-slate-700 font-medium">
                Password
              </Label>
              <div className="relative">
                <KeyRound className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <Input
                  id="admin-password"
                  type="password"
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl focus:bg-white focus:border-cyan-500 focus-visible:ring-cyan-500 text-xs"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white font-semibold text-xs transition-all duration-200 mt-2 gap-2 shadow-md shadow-cyan-600/20"
            >
              <span>{loading ? "Authenticating..." : "Sign In to Console"}</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>

        </div>
      </div>

      {/* Footer */}
      <div className="p-4 text-center text-[11px] text-slate-400 font-medium">
        Shiveda IT Services • Confidential Administrator Area • Authorized Access Only
      </div>
    </div>
  );
}
